// Recepción de mercadería desde factura de compra (o manual).
// Inventario → Operaciones → Recepción.
import { Fragment, useEffect, useMemo, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { Download, Eraser, PackagePlus, Plus, Trash2 } from 'lucide-react';
import { toast } from 'sonner';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { apiClient, getErrorMessage } from '@/lib/api/client';
import { useAuth } from '@/lib/hooks';
import { useDialogoEnLinea } from '@/components/ui/PanelIntegrado';
import {
  Campo, Location, Modal, PartnerOpt, ProductoOpt, RecepcionPrep, RecepcionResumen, Switch, TicketBuyOpt, UbicacionOpt,
  fieldCls, inv, invRpc, invalidateInventario, locationsList, money, normBuscar, num, partnersList, productosOptList, ticketsBuyList, toNum, ubicacionesList,
} from './shared';
import { DocumentoImprimible, DocColumn, DocMeta } from './DocumentoImprimible';
import { ChipSemaforo, PanelAsistente } from './RecepcionAsistente';
import type { ReactNode } from 'react';
import { fechaLocal } from '@/lib/utils/fecha';

type PrintDoc = { titulo: string; codigo: string; meta: DocMeta[]; columns: DocColumn[]; filas: ReactNode[][]; totalLabel?: string; totalValue?: ReactNode };

interface Linea {
  key: string; sri_codigo: string; sri_descripcion: string; numero_linea: number | null;
  cantidad: number; costo_unitario: number; producto_id: string;
  numero_lote: string; fecha_caducidad: string; match_origen: string | null; ubicacion_id: string;
  total_ref?: number;   // total objetivo de la línea mientras la cantidad pasa por 0 (borrar y re-tipear)
}
let seq = 0;
const nuevaLinea = (): Linea => ({
  key: `l${seq++}`, sri_codigo: '', sri_descripcion: '', numero_linea: null,
  cantidad: 1, costo_unitario: 0, producto_id: '', numero_lote: '', fecha_caducidad: '', match_origen: null, ubicacion_id: '',
});

// Respaldo/recuperación de la recepción en curso (mismo patrón del carrito POS).
interface RecepcionBorrador {
  bodegaId: string; partnerId: string; ticketId: string; referencia: string; obs: string; ocId: string; lineas: Linea[];
}

const hoy = () => fechaLocal();

// Combo buscable de producto (typeahead por palabras). El desplegable va en un portal
// (position fixed) para que la tabla con scroll no lo recorte.
function ProductoCombo({ productos, value, onChange, sugerencia }: {
  productos: ProductoOpt[]; value: string; onChange: (id: string) => void; sugerencia?: boolean;
}) {
  const [open, setOpen] = useState(false);
  const [q, setQ] = useState('');
  const [rect, setRect] = useState<DOMRect | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);
  const popRef = useRef<HTMLDivElement>(null);
  const sel = productos.find((p) => p.id === value) || null;

  const posicionar = () => { if (inputRef.current) setRect(inputRef.current.getBoundingClientRect()); };
  const abrir = () => { setQ(''); posicionar(); setOpen(true); };

  useEffect(() => {
    if (!open) return;
    const upd = () => posicionar();
    const fuera = (e: MouseEvent) => {
      const t = e.target as Node;
      if (inputRef.current?.contains(t) || popRef.current?.contains(t)) return;
      setOpen(false);
    };
    window.addEventListener('scroll', upd, true);
    window.addEventListener('resize', upd);
    document.addEventListener('mousedown', fuera);
    return () => { window.removeEventListener('scroll', upd, true); window.removeEventListener('resize', upd); document.removeEventListener('mousedown', fuera); };
  }, [open]);

  const filtrados = useMemo(() => {
    const words = normBuscar(q).split(/\s+/).filter(Boolean);
    if (!words.length) return productos.slice(0, 50);
    return productos.filter((p) => { const t = normBuscar(`${p.codigo} ${p.nombre}`); return words.every((w) => t.includes(w)); }).slice(0, 60);
  }, [q, productos]);

  const borde = !value ? 'border-amber-400' : sugerencia ? 'ring-1 ring-amber-300' : '';

  return (
    <>
      <input
        ref={inputRef}
        className={`${fieldCls} h-9 ${borde}`}
        value={open ? q : (sel ? `${sel.codigo} · ${sel.nombre}` : '')}
        placeholder="— Sin mapear — (escribe para buscar)"
        onFocus={abrir}
        onChange={(e) => { setQ(e.target.value); if (!open) { posicionar(); setOpen(true); } }}
      />
      {open && rect && createPortal(
        <div ref={popRef} style={{ position: 'fixed', top: rect.bottom + 2, left: rect.left, width: Math.max(rect.width, 300) }}
          className="z-[60] max-h-72 overflow-auto rounded border bg-background shadow-lg text-sm">
          <button type="button" className="block w-full px-2 py-1 text-left text-muted-foreground hover:bg-muted"
            onMouseDown={(e) => { e.preventDefault(); onChange(''); setOpen(false); }}>— Sin mapear —</button>
          {filtrados.length === 0 && <div className="px-2 py-2 text-muted-foreground">Sin coincidencias</div>}
          {filtrados.map((p) => (
            <button key={p.id} type="button"
              className={`block w-full px-2 py-1 text-left hover:bg-muted ${p.id === value ? 'bg-muted/60' : ''}`}
              onMouseDown={(e) => { e.preventDefault(); onChange(p.id); setOpen(false); }}>
              <span className="font-mono text-xs">{p.codigo}</span> · {p.nombre}
            </button>
          ))}
        </div>, document.body)}
    </>
  );
}

// Total de la recepción (Σ cantidad × costo unit.) en grande, arriba y abajo de las líneas.
// Si hay factura precargada, compara contra su total SIN impuestos: debe cuadrar.
function TotalBanner({ total, facturaSub, cuadra, diff, margen }: {
  total: number; facturaSub: number | null; cuadra: boolean | null; diff: number; margen: number;
}) {
  return (
    <div className={`flex flex-wrap items-center justify-between gap-3 rounded-lg border-2 px-5 py-3 ${
      cuadra === false ? 'border-red-400 bg-red-50 dark:border-red-800 dark:bg-red-900/15'
      : cuadra === true ? 'border-emerald-400 bg-emerald-50 dark:border-emerald-800 dark:bg-emerald-900/15'
      : 'border-primary/30 bg-primary/5'}`}>
      <div>
        <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Total líneas (cantidad × costo unit.)</div>
        <div className="text-3xl font-bold tabular-nums leading-tight">{money(total)}</div>
      </div>
      {facturaSub != null && (
        <div className="text-right">
          <div className="text-[11px] uppercase tracking-wide text-muted-foreground">Factura de compra · total sin impuestos</div>
          <div className="text-2xl font-semibold tabular-nums leading-tight">{money(facturaSub)}</div>
          {cuadra === true && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-semibold text-emerald-700 dark:bg-emerald-900/40 dark:text-emerald-300">✓ Cuadra con la factura (margen ±{money(margen)})</span>
          )}
          {cuadra === false && (
            <span className="mt-1 inline-flex items-center gap-1 rounded-full bg-red-100 px-2 py-0.5 text-xs font-semibold text-red-700 dark:bg-red-900/40 dark:text-red-300">✗ Difiere {money(Math.abs(diff))} (margen ±{money(margen)})</span>
          )}
        </div>
      )}
    </div>
  );
}

export function Recepcion() {
  const qc = useQueryClient();
  const dlg = useDialogoEnLinea();
  const { data: bodegas = [] } = useQuery<Location[]>({ queryKey: ['inv-locations'], queryFn: locationsList });
  const { data: productos = [] } = useQuery<ProductoOpt[]>({ queryKey: ['inv-productos-opt'], queryFn: productosOptList });
  const { data: partners = [] } = useQuery<PartnerOpt[]>({ queryKey: ['fin-partners'], queryFn: partnersList });
  const { data: compras = [] } = useQuery<TicketBuyOpt[]>({ queryKey: ['fin-tickets-buy'], queryFn: ticketsBuyList });
  const proveedores = useMemo(() => partners.filter((p) => (p.tipo_partner || []).includes('proveedor') && (p.subtipo_proveedor ?? 'inventario') === 'inventario'), [partners]);
  // Facturas de compra ya recibidas por este proceso (recepciones con tickets_buy_id).
  const { data: recibidasRows = [] } = useQuery<{ tickets_buy_id: string }[]>({
    queryKey: ['inv-recep-tickets-buy'],
    queryFn: () => inv.list<{ tickets_buy_id: string }>('recepciones', '?select=tickets_buy_id&tickets_buy_id=not.is.null'),
  });
  const recibidas = useMemo(() => new Set(recibidasRows.map((r) => r.tickets_buy_id)), [recibidasRows]);

  const principal = bodegas.find((b) => b.es_principal);
  // Política por empresa: recepción centralizada solo en la bodega principal.
  const { user } = useAuth();
  const { data: empCfg } = useQuery<{ recepcion_solo_principal: boolean; recepcion_margen_factura: number | null } | null>({
    queryKey: ['inv-recep-emp-cfg'], enabled: !!user?.empresa?.id,
    queryFn: async () => (await apiClient.get<{ recepcion_solo_principal: boolean; recepcion_margen_factura: number | null }[]>(
      `/rest/autenticacion.empresas?id=eq.${user!.empresa.id}&select=recepcion_solo_principal,recepcion_margen_factura`)).data[0] ?? null,
  });
  const soloPrincipal = empCfg?.recepcion_solo_principal ?? false;
  // Margen de aceptación en recepción vs factura compra (± USD, parametrizable por empresa)
  const margenFactura = Number(empCfg?.recepcion_margen_factura ?? 0.05);
  // Destinos válidos: si la política está activa, solo la principal; si no, toda
  // bodega real (se excluye siempre la bodega virtual EN TRÁNSITO).
  const bodegasDestino = soloPrincipal
    ? bodegas.filter((b) => b.es_principal)
    : bodegas.filter((b) => b.tipo !== 'transito');
  const [bodegaId, setBodegaId] = useState('');
  const [partnerId, setPartnerId] = useState('');
  const [ticketId, setTicketId] = useState('');
  const [referencia, setReferencia] = useState('');
  const [fecha] = useState(hoy());
  const [obs, setObs] = useState('');
  const [lineas, setLineas] = useState<Linea[]>([]);
  const [ocId, setOcId] = useState('');

  // ---- Respaldo de la recepción en curso (cierre del navegador / cambio de equipo) ----
  // localStorage al instante (mismo equipo+usuario) + borrador en el servidor cada 30s (otro equipo).
  // Mismo patrón que el carrito POS (features/pos/PuntoVenta.tsx).
  const draftKey = `inv_recepcion_borrador_${user?.empresa?.id ?? 'na'}_${user?.id ?? 'na'}`;
  const restauradoRef = useRef(false);
  const dirtyRef = useRef(false);
  const bodegaIdRef = useRef(bodegaId); bodegaIdRef.current = bodegaId;
  const partnerIdRef = useRef(partnerId); partnerIdRef.current = partnerId;
  const ticketIdRef = useRef(ticketId); ticketIdRef.current = ticketId;
  const referenciaRef = useRef(referencia); referenciaRef.current = referencia;
  const obsRef = useRef(obs); obsRef.current = obs;
  const ocIdRef = useRef(ocId); ocIdRef.current = ocId;
  const lineasRef = useRef(lineas); lineasRef.current = lineas;

  // Restaurar (una sola vez, cuando ya sabemos empresa/usuario): localStorage primero
  // (instantáneo), luego el borrador del servidor (por si fue desde otro equipo). Solo
  // si el usuario todavía no empezó a escribir nada (evita pisar lo que está tecleando).
  useEffect(() => {
    if (restauradoRef.current) return;
    if (!user?.empresa?.id) return; // espera a que cargue la sesión (la clave depende de ella)
    restauradoRef.current = true;
    if (lineasRef.current.length || partnerIdRef.current || ticketIdRef.current) return;
    const aplicar = (d: Partial<RecepcionBorrador> | null | undefined) => {
      if (!d) return;
      const tieneAlgo = (Array.isArray(d.lineas) && d.lineas.length > 0) || d.partnerId || d.ticketId;
      if (!tieneAlgo) return;
      if (Array.isArray(d.lineas) && d.lineas.length) {
        const maxSeq = d.lineas.reduce((m, l) => {
          const n = Number(String(l.key ?? '').replace(/^l/, ''));
          return Number.isFinite(n) ? Math.max(m, n) : m;
        }, -1);
        seq = Math.max(seq, maxSeq + 1);
        setLineas(d.lineas);
      }
      if (d.bodegaId) setBodegaId(d.bodegaId);
      if (d.partnerId) setPartnerId(d.partnerId);
      if (d.ticketId) setTicketId(d.ticketId);
      if (d.referencia) setReferencia(d.referencia);
      if (d.obs) setObs(d.obs);
      if (d.ocId) setOcId(d.ocId);
      toast.info(`Se recuperó una recepción en curso${d.lineas?.length ? ` (${d.lineas.length} línea${d.lineas.length === 1 ? '' : 's'})` : ''}`);
    };
    try {
      const raw = localStorage.getItem(draftKey);
      if (raw) { aplicar(JSON.parse(raw)); return; }
    } catch { /* respaldo local ilegible: se ignora */ }
    invRpc<RecepcionBorrador | null>('inv_recepcion_borrador_obtener', {})
      .then((d) => { if (!lineasRef.current.length && !partnerIdRef.current && !ticketIdRef.current) aplicar(d); })
      .catch(() => {});
  }, [draftKey, user?.empresa?.id]);

  // Autoguardado: cada cambio se marca "sucio" y se respalda en localStorage al instante.
  useEffect(() => {
    if (!restauradoRef.current) return;
    dirtyRef.current = true;
    const tieneAlgo = lineas.length > 0 || !!partnerId || !!ticketId;
    try {
      if (tieneAlgo) localStorage.setItem(draftKey, JSON.stringify({ bodegaId, partnerId, ticketId, referencia, obs, ocId, lineas }));
      else localStorage.removeItem(draftKey);
    } catch { /* cuota llena: el respaldo local es best-effort */ }
  }, [bodegaId, partnerId, ticketId, referencia, obs, ocId, lineas, draftKey]);

  // Cada 30s, si hay cambios sin respaldar en el servidor, los guarda.
  useEffect(() => {
    const id = setInterval(() => {
      if (!dirtyRef.current) return;
      dirtyRef.current = false;
      const tieneAlgo = lineasRef.current.length > 0 || !!partnerIdRef.current || !!ticketIdRef.current;
      const payload = tieneAlgo ? {
        bodegaId: bodegaIdRef.current, partnerId: partnerIdRef.current, ticketId: ticketIdRef.current,
        referencia: referenciaRef.current, obs: obsRef.current, ocId: ocIdRef.current, lineas: lineasRef.current,
      } : null;
      invRpc('inv_recepcion_borrador_guardar', { _payload: payload }).catch(() => { dirtyRef.current = true; });
    }, 30000);
    return () => clearInterval(id);
  }, []);

  // Al confirmar la recepción: limpiar el respaldo YA (local y servidor), sin esperar el ciclo.
  const limpiarRespaldo = () => {
    try { localStorage.removeItem(draftKey); } catch { /* best-effort */ }
    dirtyRef.current = false;
    void invRpc('inv_recepcion_borrador_guardar', { _payload: null }).catch(() => {});
  };

  // Botón "Limpiar" (replicado arriba y abajo): descarta la transacción en curso
  // Y su respaldo local/servidor (sin esto el borrador la "recuperaría" al recargar).
  const hayTransaccion = lineas.length > 0 || !!partnerId || !!ticketId || !!ocId || !!referencia || !!obs;
  const limpiarTodo = async () => {
    if (!(await dlg.confirmar('¿Limpiar toda la transacción?', {
      detalle: 'Se descartan las líneas cargadas, el proveedor, la factura/OC seleccionada y el respaldo. Esta acción no se puede deshacer.',
      peligro: true,
    }))) return;
    setLineas([]); setPartnerId(''); setTicketId(''); setOcId(''); setReferencia(''); setObs('');
    limpiarRespaldo();
    toast.info('Transacción limpiada — puedes empezar una recepción nueva');
  };

  interface OcOpt { oc_id: string; codigo: string; proveedor: string; proveedor_id: string; location_id: string; bodega: string; pedido: number; recibido: number }
  const { data: ocsAbiertas = [] } = useQuery<OcOpt[]>({
    queryKey: ['inv-ocs-abiertas'],
    queryFn: async () => {
      const a = await invRpc<OcOpt[]>('inv_oc_listar', { _estado: 'aprobada' });
      const b = await invRpc<OcOpt[]>('inv_oc_listar', { _estado: 'recibida_parcial' });
      return [...a, ...b];
    },
    staleTime: 0,
  });
  const cargarOc = useMutation({
    mutationFn: (id: string) => invRpc<{ proveedor_id: string; location_id: string; lineas: { producto_id: string; codigo: string; producto: string; pendiente: number; costo_est: number }[] }>('inv_oc_obtener', { _oc: id }),
    onSuccess: (oc) => {
      setPartnerId(oc.proveedor_id); setBodegaId(oc.location_id); setTicketId('');
      setLineas(oc.lineas.filter((l) => Number(l.pendiente) > 0).map((l) => ({
        key: `l${seq++}`, sri_codigo: l.codigo, sri_descripcion: l.producto, numero_linea: null,
        cantidad: Number(l.pendiente), costo_unitario: Number(l.costo_est) || 0,
        producto_id: l.producto_id, numero_lote: '', fecha_caducidad: '', match_origen: 'oc', ubicacion_id: '',
      })));
      toast.success(`Orden cargada: ${oc.lineas.length} líneas (cantidades = pendiente por recibir)`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const destino = soloPrincipal ? (principal?.id || '') : (bodegaId || principal?.id || '');
  const { data: ubicaciones = [] } = useQuery<UbicacionOpt[]>({ queryKey: ['inv-ubic', destino], queryFn: () => ubicacionesList(destino), enabled: !!destino });

  const prodById = (id: string) => productos.find((p) => p.id === id);
  const setLinea = (key: string, patch: Partial<Linea>) =>
    setLineas((ls) => ls.map((l) => (l.key === key ? { ...l, ...patch } : l)));

  // Cargar líneas desde una factura de compra (auto-mapeo)
  const cargar = useMutation({
    mutationFn: () => invRpc<RecepcionPrep>('inv_recepcion_preparar', { p_tickets_buy_id: ticketId }),
    onSuccess: (prep) => {
      if (prep.partner_id) setPartnerId(prep.partner_id);
      if (prep.referencia) setReferencia(prep.referencia);
      if (!bodegaId && prep.location_principal_id) setBodegaId(prep.location_principal_id);
      setLineas((prep.lineas || []).map((pl) => ({
        key: `l${seq++}`, sri_codigo: pl.codigo_principal || pl.codigo_auxiliar || '',
        sri_descripcion: pl.descripcion, numero_linea: pl.numero_linea,
        cantidad: Number(pl.cantidad) || 0, costo_unitario: Number(pl.costo_unitario) || 0,
        producto_id: pl.producto_id || '', numero_lote: '', fecha_caducidad: '', match_origen: pl.match_origen, ubicacion_id: '',
      })));
      const sinMapeo = (prep.lineas || []).filter((l) => !l.producto_id).length;
      toast.success(`Cargadas ${prep.lineas?.length ?? 0} líneas${sinMapeo ? ` · ${sinMapeo} sin mapear` : ''}`);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  const confirmar = useMutation({
    // auth: autorización de descuadre (usuario nivel ≥3 + contraseña + observación).
    // El servidor la valida y la registra en auditoría; la clave nunca se guarda.
    mutationFn: async (auth?: { usuario: string; password: string; observacion: string }) => {
      const usables = lineas.filter((l) => l.producto_id && Number(l.cantidad) > 0);
      if (usables.length === 0) throw new Error('No hay líneas con producto y cantidad válidos');
      const ticket = compras.find((c) => c.id === ticketId);
      const payload = {
        ...(auth ? { autorizacion: auth } : {}),
        location_id: destino || null,
        partner_id: partnerId || null,
        tickets_buy_id: ticketId || null,
        oc_id: ocId || null,
        documento_id: ticket?.documento_id || null,
        referencia: referencia || null,
        fecha, observaciones: obs || null,
        lineas: usables.map((l) => ({
          producto_id: l.producto_id, cantidad: Number(l.cantidad), costo_unitario: Number(l.costo_unitario),
          numero_lote: l.numero_lote || null, fecha_caducidad: l.fecha_caducidad || null,
          sri_codigo: l.sri_codigo || null, sri_descripcion: l.sri_descripcion || null, numero_linea: l.numero_linea,
          ubicacion_id: l.ubicacion_id || null,
          guardar_mapeo: true,
        })),
      };
      return invRpc<{
        recepcion_id: string; codigo: string; total_lineas: number;
        avisos_precio?: { producto: string; variacion_pct: number; impacto_usd: number; semaforo: string }[];
        avisos_precio_n?: number;
      }>('inv_recepcion_confirmar', { payload });
    },
    onSuccess: (r) => {
      setAuthOpen(false);
      toast.success(`Recepción ${r.codigo} confirmada (${r.total_lineas} líneas)`);
      // El control de precios avisa, no bloquea: la recepción ya pasó
      if (r.avisos_precio_n && r.avisos_precio_n > 0) {
        const tot = (r.avisos_precio ?? []).reduce((s, a) => s + Number(a.impacto_usd || 0), 0);
        toast.warning(
          `${r.avisos_precio_n} línea(s) con precio fuera de lo acordado · impacto ${money(tot)}. ` +
          'Quedaron en Precios de compra → Alertas para que compras las revise.',
          { duration: 12000 },
        );
      }
      setOcId('');
      const usables = lineas.filter((l) => l.producto_id && Number(l.cantidad) > 0);
      const prodLabel = (id: string) => { const p = productos.find((x) => x.id === id); return p ? `${p.codigo} · ${p.nombre}` : id; };
      setPrintDoc({
        titulo: 'Recepción de mercadería', codigo: r.codigo,
        meta: [
          { label: 'Fecha', value: fecha },
          { label: 'Bodega', value: bodegas.find((b) => b.id === destino)?.nombre ?? '—' },
          { label: 'Proveedor', value: proveedores.find((p) => p.id === partnerId)?.razon_social ?? '—' },
          { label: 'Referencia', value: referencia || '—' },
        ],
        columns: [{ header: 'Producto' }, { header: 'Cantidad', right: true }, { header: 'Costo unit.', right: true }, { header: 'Lote' }, { header: 'Caducidad' }],
        filas: usables.map((l) => [prodLabel(l.producto_id), num(l.cantidad, 2), money(l.costo_unitario), l.numero_lote || '—', l.fecha_caducidad || '—']),
        totalLabel: 'Total', totalValue: money(total),
      });
      setLineas([]); setTicketId(''); setReferencia(''); setObs('');
      limpiarRespaldo();
      invalidateInventario(qc);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  // Crear producto / variante al vuelo
  const [crearKey, setCrearKey] = useState<string | null>(null);
  const [crearVarKey, setCrearVarKey] = useState<string | null>(null);
  const [asistenteKey, setAsistenteKey] = useState<string | null>(null);
  const [authOpen, setAuthOpen] = useState(false);  // modal de autorización de descuadre
  const [printDoc, setPrintDoc] = useState<PrintDoc | null>(null);
  const total = useMemo(() => lineas.reduce((s, l) => s + Number(l.cantidad) * Number(l.costo_unitario), 0), [lineas]);

  // Validación contra la factura precargada: Σ(cantidad × costo) debe cuadrar con el
  // total SIN impuestos de la factura (tickets_buy.subtotal; respaldo: total − IVA).
  // La tolerancia es el "Margen de aceptación en recepción vs factura compra" de la empresa.
  const facturaSel = useMemo(() => compras.find((c) => c.id === ticketId) || null, [compras, ticketId]);
  const facturaSub = useMemo(() => {
    if (!facturaSel) return null;
    if (facturaSel.subtotal != null) return Number(facturaSel.subtotal);
    if (facturaSel.importe_total != null) return Number(facturaSel.importe_total) - Number(facturaSel.iva ?? 0);
    return null;
  }, [facturaSel]);
  const diffFactura = facturaSub != null ? total - facturaSub : 0;
  const cuadraFactura = facturaSub == null || lineas.length === 0 ? null
    : Math.abs(diffFactura) <= margenFactura;

  // Facturas precargables: solo del proveedor elegido (si hay) y que NO se hayan recibido aún.
  const comprasDisponibles = useMemo(
    () => compras.filter((c) => !recibidas.has(c.id) && (!partnerId || c.partner_id === partnerId)),
    [compras, recibidas, partnerId],
  );
  // Al cambiar de proveedor (o si la factura ya se recibió), limpia la selección inválida.
  useEffect(() => {
    if (ticketId && !comprasDisponibles.some((c) => c.id === ticketId)) setTicketId('');
  }, [comprasDisponibles, ticketId]);

  // ---- Control preventivo de precios: semáforo por línea ANTES de confirmar ----
  // Consulta el baseline acordado y avisa; nunca bloquea (el camión está en el muelle).
  const lineasParaCheck = useMemo(
    () => lineas.filter((l) => l.producto_id && Number(l.cantidad) > 0)
      .map((l) => ({ producto_id: l.producto_id, cantidad: Number(l.cantidad), costo_unitario: Number(l.costo_unitario) })),
    [lineas],
  );
  const { data: chequeo } = useQuery<{
    lineas: { producto_id: string; producto: string; semaforo: string; baseline: number | null; fuente: string;
      variacion_pct: number | null; impacto_usd: number; tolerancia_pct: number | null; clase: string }[];
    con_aviso: number; impacto_total: number;
  }>({
    queryKey: ['inv-precio-check', partnerId, JSON.stringify(lineasParaCheck)],
    queryFn: () => invRpc('inv_precio_compra_check', { _payload: { partner_id: partnerId || null, lineas: lineasParaCheck } }),
    enabled: !!partnerId && lineasParaCheck.length > 0,
    staleTime: 5000,
  });
  const avisoDe = (productoId: string) => chequeo?.lineas.find((c) => c.producto_id === productoId);

  const fijarAcordado = useMutation({
    mutationFn: (p: { producto_id: string; precio: number }) =>
      invRpc('inv_precio_acordado_set', { _partner_id: partnerId, _producto_id: p.producto_id, _precio: p.precio,
        _nota: 'Fijado desde la recepción' }),
    onSuccess: () => { toast.success('Precio acordado actualizado'); void qc.invalidateQueries({ queryKey: ['inv-precio-check'] }); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  const sinMapear = lineas.filter((l) => !l.producto_id).length;

  // Resumen de cabecera de la factura cargada: líneas/mapeadas/alertas/impacto.
  const { data: resumen, isFetching: resumenFetching, refetch: refetchResumen } = useQuery<RecepcionResumen>({
    queryKey: ['inv-recep-resumen', ticketId],
    queryFn: () => invRpc('inv_recepcion_resumen', { _tickets_buy_id: ticketId, _location: null }),
    enabled: !!ticketId,
    staleTime: 0,
  });

  // Validación de cabecera/líneas (la RPC vuelve a validar en el servidor)
  const faltantes = useMemo(() => {
    const f: string[] = [];
    if (!partnerId) f.push('proveedor');
    if (!destino) f.push('bodega destino');
    if (!fecha) f.push('fecha');
    else if (fecha > hoy()) f.push('fecha (no puede ser futura)');
    if (!lineas.some((l) => l.producto_id && Number(l.cantidad) > 0)) f.push('al menos una línea con producto y cantidad');
    return f;
  }, [partnerId, destino, fecha, lineas]);

  return (
    <div className="p-6 space-y-4">
      {dlg.ui}
      <div className="flex items-center gap-3">
        <h1 className="text-xl font-semibold flex items-center gap-2"><PackagePlus className="w-5 h-5" /> Recepción de mercadería</h1>
        {hayTransaccion && (
          <Button variant="outline" className="ml-auto" onClick={limpiarTodo}><Eraser className="w-4 h-4 mr-1" /> Limpiar</Button>
        )}
      </div>

      {bodegas.length === 0 && (
        <div className="rounded-md border border-amber-300 bg-amber-50 px-4 py-3 text-sm text-amber-800 dark:bg-amber-900/20 dark:text-amber-200">
          No hay bodegas activas. Crea una en <span className="font-medium">Inventario → Bodegas</span> antes de recibir mercadería.
        </div>
      )}

      {/* Control preventivo de precios: avisa antes de confirmar, no bloquea */}
      {!!chequeo?.con_aviso && (
        <div className="rounded-md border border-amber-400 bg-amber-50 px-4 py-3 text-sm text-amber-900 dark:border-amber-700 dark:bg-amber-900/20 dark:text-amber-200">
          <div className="font-medium">
            {chequeo.con_aviso} línea(s) con precio fuera de lo acordado · impacto {money(chequeo.impacto_total)}
          </div>
          <p className="pt-0.5 text-xs">
            Puedes recibir igual (la mercadería entra al inventario). Las líneas marcadas quedan como alerta para que
            compras las revise, y el costo con que se costean las recetas <b>no se actualiza</b> hasta que alguien
            apruebe el nuevo precio.
          </p>
        </div>
      )}

      <Card>
        <CardHeader className="pb-2"><CardTitle className="text-base">Cabecera</CardTitle></CardHeader>
        <CardContent className="grid grid-cols-2 md:grid-cols-3 gap-3">
          <Campo label="Bodega destino *">
            <select className={`${fieldCls}${soloPrincipal ? ' bg-muted/50 cursor-not-allowed' : ''}`} value={destino} disabled={soloPrincipal} onChange={(e) => setBodegaId(e.target.value)}>
              {!principal && <option value="">— Selecciona —</option>}
              {bodegasDestino.map((b) => <option key={b.id} value={b.id}>{b.nombre}{b.es_principal ? ' (principal)' : ''}</option>)}
            </select>
            {soloPrincipal && <p className="mt-1 text-xs text-muted-foreground">Esta empresa recibe solo en la <b>Bodega Principal</b>; distribuye a las sucursales por Transferencias.</p>}
          </Campo>
          <Campo label="Proveedor *">
            <select className={fieldCls} value={partnerId} onChange={(e) => setPartnerId(e.target.value)}>
              <option value="">— Selecciona —</option>
              {proveedores.map((p) => <option key={p.id} value={p.id}>{p.razon_social} ({p.identificacion ?? 's/i'})</option>)}
            </select>
          </Campo>
          <Campo label="Fecha">
            <input className={`${fieldCls} bg-muted/50 cursor-not-allowed`} value={fecha} readOnly title="La fecha de la recepción es siempre hoy (control de costos). La factura de compra conserva su propia fecha en la CxP." />
          </Campo>
          <Campo label={`Factura de compra (precargar)${partnerId ? ' — del proveedor · pendientes' : ' — pendientes'}`} className="md:col-span-2">
            <div className="flex gap-2">
              <select className={fieldCls} value={ticketId} onChange={(e) => setTicketId(e.target.value)}>
                <option value="">— Recepción manual —</option>
                {comprasDisponibles.map((c) => <option key={c.id} value={c.id}>{c.serie_comprobante ?? c.id.slice(0, 8)} · {c.razon_social_emisor ?? ''} · {c.fecha_emision ?? ''} · {money(c.importe_total)}</option>)}
              </select>
              <Button variant="outline" disabled={!ticketId} isLoading={cargar.isPending} onClick={() => cargar.mutate()}><Download className="w-4 h-4 mr-1" /> Cargar</Button>
            </div>
            {partnerId && comprasDisponibles.length === 0 && (
              <span className="mt-1 block text-xs text-muted-foreground">Este proveedor no tiene facturas de compra pendientes de recibir.</span>
            )}
          </Campo>
          <Campo label="Orden de compra (precargar pendiente)" className="md:col-span-2">
            <div className="flex gap-2">
              <select className={fieldCls} value={ocId} onChange={(e) => setOcId(e.target.value)}>
                <option value="">— Sin orden de compra —</option>
                {ocsAbiertas.map((o) => <option key={o.oc_id} value={o.oc_id}>{o.codigo} · {o.proveedor} · {o.bodega} · recibido {Number(o.recibido)}/{Number(o.pedido)}</option>)}
              </select>
              <Button variant="outline" disabled={!ocId} isLoading={cargarOc.isPending} onClick={() => cargarOc.mutate(ocId)}><Download className="w-4 h-4 mr-1" /> Cargar</Button>
            </div>
          </Campo>
          <Campo label="Referencia"><input className={fieldCls} value={referencia} onChange={(e) => setReferencia(e.target.value)} /></Campo>
        </CardContent>
      </Card>

      {ticketId && (
        <div className="flex flex-wrap items-center gap-3 rounded-md border bg-muted/20 px-4 py-2 text-sm">
          {resumen ? (
            <>
              <span>{resumen.lineas_total} línea(s)</span>
              <span className="text-muted-foreground">·</span>
              <span>{resumen.mapeadas} mapeada(s)</span>
              <span className="text-muted-foreground">·</span>
              <span className={resumen.alerta_costo > 0 ? 'font-semibold text-red-600 dark:text-red-400' : ''}>
                {resumen.alerta_costo} en alerta de costo
              </span>
              <span className="text-muted-foreground">·</span>
              <span className={resumen.alerta_rentabilidad > 0 ? 'font-semibold text-amber-600 dark:text-amber-400' : ''}>
                {resumen.alerta_rentabilidad} en alerta de rentabilidad
              </span>
              <span className="text-muted-foreground">·</span>
              <span>impacto {money(resumen.impacto_valor)}</span>
            </>
          ) : (
            <span className="text-muted-foreground">Cargando resumen…</span>
          )}
          <Button size="sm" variant="outline" className="ml-auto" isLoading={resumenFetching} onClick={() => void refetchResumen()}>
            Actualizar
          </Button>
        </div>
      )}

      {lineas.length > 0 && <TotalBanner total={total} facturaSub={facturaSub} cuadra={cuadraFactura} diff={diffFactura} margen={margenFactura} />}

      <Card>
        <CardHeader className="pb-2 flex flex-row items-center justify-between">
          <CardTitle className="text-base">
            Líneas ({lineas.length}){sinMapear > 0 && <span className="ml-2 text-xs text-amber-600">· {sinMapear} sin producto</span>}
          </CardTitle>
          <Button variant="outline" size="sm" onClick={() => setLineas((ls) => [...ls, nuevaLinea()])}><Plus className="w-4 h-4 mr-1" /> Línea</Button>
        </CardHeader>
        <CardContent className="overflow-x-auto">
          <table className="w-full text-sm">
            <thead><tr className="border-b text-left text-xs text-muted-foreground">
              <th className="py-2 pr-2">Descripción / código SRI</th><th className="py-2 pr-2 w-72">Producto</th>
              <th className="py-2 pr-2 w-24 text-right">Cantidad</th><th className="py-2 pr-2 w-28 text-right">Costo unit.</th>
              <th className="py-2 pr-2 w-32 text-right">Subtotal</th><th className="py-2 w-10"></th>
            </tr></thead>
            <tbody>
              {lineas.length === 0 && <tr><td colSpan={6} className="py-8 text-center text-muted-foreground">Carga una factura o agrega líneas</td></tr>}
              {lineas.map((l) => {
                const p = prodById(l.producto_id);
                const lote = p?.maneja_lote || p?.maneja_caducidad;
                const asistenteAbierto = asistenteKey === l.key;
                return (
                  <Fragment key={l.key}>
                    <tr className="align-top">
                      <td className="py-2 pr-2">
                        <input className={`${fieldCls} h-9`} placeholder="Descripción" value={l.sri_descripcion} onChange={(e) => setLinea(l.key, { sri_descripcion: e.target.value })} />
                        {l.sri_codigo && <span className="block mt-1 text-xs font-mono text-muted-foreground">cód: {l.sri_codigo}</span>}
                        {l.producto_id && l.match_origen && (() => {
                          const suger = l.match_origen === 'nombre' || l.match_origen === 'similar';
                          const lbl = l.match_origen === 'barras' ? 'código' : l.match_origen === 'oc' ? 'orden compra' : l.match_origen === 'nombre' ? 'por nombre' : l.match_origen === 'similar' ? 'por similitud' : l.match_origen === 'manual' ? 'manual' : l.match_origen;
                          return <span className={`mt-1 inline-flex items-center gap-0.5 rounded border px-1 text-[10px] ${suger ? 'border-amber-400 text-amber-600' : 'border-emerald-400 text-emerald-600'}`}>{suger ? '⚠' : '✓'} {lbl}</span>;
                        })()}
                        {p && <span className="block mt-0.5 text-xs text-muted-foreground">Presentación: {p.presentacion || '—'} · Fracc.: {p.fracciones ?? '—'}</span>}
                      </td>
                      <td className="py-2 pr-2">
                        <ProductoCombo productos={productos} value={l.producto_id}
                          sugerencia={l.match_origen === 'nombre' || l.match_origen === 'similar'}
                          onChange={(id) => setLinea(l.key, { producto_id: id, match_origen: 'manual' })} />
                        <div className="mt-1 flex gap-3">
                          <button className="text-xs text-primary hover:underline" onClick={() => setCrearKey(l.key)}>+ crear producto</button>
                          <button className="text-xs text-primary hover:underline" onClick={() => setCrearVarKey(l.key)}>+ crear variante</button>
                        </div>
                        {p && (
                          <ChipSemaforo producto={p} costo={Number(l.costo_unitario)} cantidad={Number(l.cantidad)}
                            open={asistenteAbierto}
                            onToggle={() => setAsistenteKey((k) => (k === l.key ? null : l.key))} />
                        )}
                      </td>
                      {/* Al cambiar la CANTIDAD, el costo unitario se recalcula para PRESERVAR el
                          total de la línea (regla de negocio: la factura manda el total; ej. 10u×$1
                          → 20u pasa a $0.50). Editar el COSTO directamente sigue siendo libre. */}
                      <td className="py-2 pr-2"><input type="number" step="0.0001" min="0" className={`${fieldCls} h-9 text-right`} value={l.cantidad} onChange={(e) => {
                        const nueva = toNum(e.target.value);
                        const totalPrev = (Number(l.cantidad) || 0) * (Number(l.costo_unitario) || 0);
                        const objetivo = totalPrev > 0 ? totalPrev : (l.total_ref ?? 0);   // sobrevive a borrar y re-tipear
                        if (nueva > 0 && objetivo > 0) setLinea(l.key, { cantidad: nueva, costo_unitario: Math.round((objetivo / nueva) * 1e6) / 1e6, total_ref: undefined });
                        else setLinea(l.key, { cantidad: nueva, ...(totalPrev > 0 ? { total_ref: totalPrev } : {}) });
                      }} /></td>
                      <td className="py-2 pr-2">
                        {(() => {
                          const av = avisoDe(l.producto_id);
                          const alerta = av && av.semaforo !== 'verde';
                          return (
                            <>
                              <input type="number" step="0.0001" min="0"
                                className={`${fieldCls} h-9 text-right ${alerta
                                  ? av!.semaforo === 'rojo'
                                    ? 'border-red-500 bg-red-50 dark:bg-red-900/20'
                                    : 'border-amber-500 bg-amber-50 dark:bg-amber-900/20' : ''}`}
                                value={l.costo_unitario}
                                onChange={(e) => setLinea(l.key, { costo_unitario: toNum(e.target.value), total_ref: undefined })} />
                              {alerta && (
                                <div className={`mt-1 text-[11px] leading-tight ${av!.semaforo === 'rojo'
                                  ? 'text-red-700 dark:text-red-400' : 'text-amber-700 dark:text-amber-400'}`}>
                                  {Number(av!.variacion_pct) > 0 ? '▲' : '▼'} {num(Math.abs(Number(av!.variacion_pct)), 1)}%
                                  vs {av!.fuente === 'acordado' ? 'acordado' : av!.fuente} {money(av!.baseline ?? 0)}
                                  {' · '}impacto {money(av!.impacto_usd)}
                                  <button className="ml-1 underline"
                                    onClick={() => fijarAcordado.mutate({ producto_id: l.producto_id, precio: Number(l.costo_unitario) })}>
                                    fijar como acordado
                                  </button>
                                </div>
                              )}
                            </>
                          );
                        })()}
                      </td>
                      <td className="py-2 pr-2 text-right">
                        <span className="inline-block pt-2 font-semibold tabular-nums">{money(Number(l.cantidad) * Number(l.costo_unitario))}</span>
                      </td>
                      <td className="py-2 text-right"><button className="inline-flex w-7 h-7 items-center justify-center rounded-md text-red-500 hover:bg-red-50 dark:hover:bg-red-900/20" onClick={() => setLineas((ls) => ls.filter((x) => x.key !== l.key))}><Trash2 className="w-4 h-4" /></button></td>
                    </tr>
                    {/* Segunda línea del renglón: lote / caducidad / ubicación */}
                    <tr className="border-b last:border-0">
                      <td colSpan={6} className="pb-2 pl-1">
                        <div className="flex flex-wrap items-center gap-x-4 gap-y-1 text-xs">
                          <label className="flex items-center gap-1.5 text-muted-foreground">Lote
                            <input className={`${fieldCls} h-8 w-36`} placeholder={lote ? 'lote' : '—'} value={l.numero_lote} onChange={(e) => setLinea(l.key, { numero_lote: e.target.value })} />
                          </label>
                          <label className="flex items-center gap-1.5 text-muted-foreground">Caducidad
                            <input type="date" className={`${fieldCls} h-8 w-40`} value={l.fecha_caducidad} onChange={(e) => setLinea(l.key, { fecha_caducidad: e.target.value })} />
                          </label>
                          <label className="flex items-center gap-1.5 text-muted-foreground">Ubicación
                            <select className={`${fieldCls} h-8 w-56`} value={l.ubicacion_id} onChange={(e) => setLinea(l.key, { ubicacion_id: e.target.value })}>
                              <option value="">General (por defecto)</option>
                              {ubicaciones.map((u) => <option key={u.id} value={u.id}>{u.codigo}{u.nombre ? ` — ${u.nombre}` : ''}</option>)}
                            </select>
                          </label>
                        </div>
                      </td>
                    </tr>
                    {p && asistenteAbierto && (
                      <tr className="border-b bg-muted/20">
                        <td colSpan={6} className="p-3">
                          <PanelAsistente producto={p} costoLinea={Number(l.costo_unitario)} cantidadLinea={Number(l.cantidad)}
                            partnerId={partnerId} ticketsBuyId={ticketId} />
                        </td>
                      </tr>
                    )}
                  </Fragment>
                );
              })}
            </tbody>
          </table>
        </CardContent>
      </Card>

      {lineas.length > 0 && <TotalBanner total={total} facturaSub={facturaSub} cuadra={cuadraFactura} diff={diffFactura} margen={margenFactura} />}

      <div className="flex items-center justify-end gap-4">
        <div className="flex items-center gap-3">
          {hayTransaccion && (
            <Button variant="outline" onClick={limpiarTodo}><Eraser className="w-4 h-4 mr-1" /> Limpiar</Button>
          )}
          {faltantes.length > 0 && (
            <span className="text-xs text-amber-600 dark:text-amber-400">Falta: {faltantes.join(', ')}</span>
          )}
          <Button isLoading={confirmar.isPending} disabled={faltantes.length > 0} onClick={async () => {
            const n = lineas.filter((l) => l.producto_id && Number(l.cantidad) > 0).length;
            // Descuadre fuera del margen: exige autorización de un usuario nivel ≥3 (modal).
            if (cuadraFactura === false) { setAuthOpen(true); return; }
            if (await dlg.confirmar(`¿Confirmar la recepción de ${n} línea(s) en «${bodegas.find((b) => b.id === destino)?.nombre ?? ''}»?`, { detalle: 'Se actualizará el stock.' })) confirmar.mutate(undefined);
          }}>Confirmar recepción</Button>
        </div>
      </div>

      {crearKey && (
        <CrearProductoModal
          onClose={() => setCrearKey(null)}
          onCreated={(id) => { void qc.invalidateQueries({ queryKey: ['inv-productos-opt'] }); setLinea(crearKey, { producto_id: id }); setCrearKey(null); }}
        />
      )}

      {crearVarKey && (
        <CrearVarianteModal
          onClose={() => setCrearVarKey(null)}
          onCreated={(id) => { void qc.invalidateQueries({ queryKey: ['inv-productos-opt'] }); setLinea(crearVarKey, { producto_id: id }); setCrearVarKey(null); }}
        />
      )}

      {authOpen && facturaSub != null && (
        <AutorizarDescuadreModal total={total} facturaSub={facturaSub} diff={diffFactura} margen={margenFactura}
          isPending={confirmar.isPending} onClose={() => setAuthOpen(false)}
          onAutorizar={(a) => confirmar.mutate(a)} />
      )}

      {printDoc && <DocumentoImprimible {...printDoc} onClose={() => setPrintDoc(null)} />}
    </div>
  );
}

// Autorización de descuadre vs factura: usuario nivel ≥3 + contraseña + observación.
// El servidor valida credenciales/nivel y registra el log en inventario.auditoria.
function AutorizarDescuadreModal({ total, facturaSub, diff, margen, isPending, onClose, onAutorizar }: {
  total: number; facturaSub: number; diff: number; margen: number; isPending: boolean;
  onClose: () => void; onAutorizar: (a: { usuario: string; password: string; observacion: string }) => void;
}) {
  const [usuario, setUsuario] = useState('');
  const [password, setPassword] = useState('');
  const [observacion, setObservacion] = useState('');
  const ok = !!usuario.trim() && !!password && !!observacion.trim();
  return (
    <Modal title="Autorización requerida — descuadre vs factura" onClose={onClose}>
      <div className="space-y-3">
        <div className="rounded-md border border-red-300 bg-red-50 p-3 text-sm dark:border-red-800 dark:bg-red-900/15">
          <div className="flex justify-between"><span>Total líneas (cantidad × costo)</span><b className="tabular-nums">{money(total)}</b></div>
          <div className="flex justify-between"><span>Factura de compra · sin impuestos</span><b className="tabular-nums">{money(facturaSub)}</b></div>
          <div className="flex justify-between font-semibold text-red-700 dark:text-red-300"><span>Diferencia (margen permitido ±{money(margen)})</span><b className="tabular-nums">{money(Math.abs(diff))}</b></div>
        </div>
        <p className="text-xs text-muted-foreground">
          Debe autorizar un usuario de esta empresa con <b>nivel 3 o superior</b>. La autorización queda en el
          <b> log de auditoría</b> (fecha/hora, autorizante y motivo) y la observación se anexa al documento de recepción.
        </p>
        <Campo label="Usuario autorizante (usuario o correo) *">
          <input className={fieldCls} value={usuario} onChange={(e) => setUsuario(e.target.value)} autoFocus autoComplete="off" />
        </Campo>
        <Campo label="Contraseña *">
          <input type="password" className={fieldCls} value={password} onChange={(e) => setPassword(e.target.value)} autoComplete="new-password" />
        </Campo>
        <Campo label="Observación / motivo *">
          <textarea className={`${fieldCls} h-20`} value={observacion} onChange={(e) => setObservacion(e.target.value)}
            placeholder="Ej.: faltó una caja en el camión; el proveedor enviará nota de crédito" />
        </Campo>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={isPending} disabled={!ok}
            onClick={() => onAutorizar({ usuario: usuario.trim(), password, observacion: observacion.trim() })}>
            Autorizar y confirmar recepción
          </Button>
        </div>
      </div>
    </Modal>
  );
}

// Crear una variante nueva (color/talla) de un modelo existente, sin salir de la recepción.
interface AtributoPlantilla { id: string; codigo: string | null; nombre: string; valores: { id: string; atributo_id: string; valor: string; abreviatura: string | null }[] }

function CrearVarianteModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [padreId, setPadreId] = useState('');
  const [seleccion, setSeleccion] = useState<Record<string, string>>({}); // atributo_id -> valor_id

  const { data: padres = [] } = useQuery<{ id: string; codigo: string; nombre: string; categoria_id: string | null }[]>({
    queryKey: ['inv-padres-variantes'],
    queryFn: () => inv.list('productos', '?select=id,codigo,nombre,categoria_id&tiene_variantes=eq.true&deleted_at=is.null&order=codigo'),
  });
  const padre = padres.find((p) => p.id === padreId);

  const { data: atributos = [] } = useQuery<AtributoPlantilla[]>({
    queryKey: ['inv-plantilla-atributos', padre?.categoria_id],
    enabled: !!padre?.categoria_id,
    queryFn: async (): Promise<AtributoPlantilla[]> => {
      const enlaces = await inv.list<{ atributo_id: string; orden: number }>(
        'categoria_atributos', `?select=atributo_id,orden&categoria_id=eq.${padre!.categoria_id}&order=orden`);
      if (!enlaces.length) return [];
      const ids = enlaces.map((e) => e.atributo_id).join(',');
      const attrs = await inv.list<{ id: string; codigo: string | null; nombre: string }>(
        'atributos', `?select=id,codigo,nombre&id=in.(${ids})`);
      const valores = await inv.list<{ id: string; atributo_id: string; valor: string; abreviatura: string | null }>(
        'atributo_valores', `?select=id,atributo_id,valor,abreviatura&atributo_id=in.(${ids})&es_activo=eq.true&order=orden`);
      return enlaces
        .map((e) => {
          const a = attrs.find((x) => x.id === e.atributo_id);
          return a ? { ...a, valores: valores.filter((v) => v.atributo_id === a.id) } : null;
        })
        .filter((x): x is AtributoPlantilla => x !== null);
    },
  });

  const completo = !!padreId && atributos.length > 0 && atributos.every((a) => seleccion[a.id]);

  const crear = useMutation({
    mutationFn: async () => {
      // Un solo combo = la combinación elegida, en el orden de la plantilla
      const combo = atributos.map((a) => ({ atributo_id: a.id, valor_id: seleccion[a.id] }));
      const r = await invRpc<{ creadas: number; omitidas: number }>('variantes_generar', { _maestro_id: padreId, _combos: [combo] });
      // Código determinístico (misma regla del servidor: abreviatura o 3 primeras letras)
      const abrevs = atributos.map((a) => {
        const v = a.valores.find((x) => x.id === seleccion[a.id])!;
        return (v.abreviatura ?? '').trim() || v.valor.slice(0, 3).toUpperCase();
      });
      const codigo = `${padre!.codigo}-${abrevs.join('-')}`;
      const rows = await inv.list<{ id: string; codigo: string; nombre: string }>(
        'productos', `?select=id,codigo,nombre&codigo=eq.${encodeURIComponent(codigo)}&deleted_at=is.null`);
      if (!rows.length) throw new Error(`No se encontró la variante generada (${codigo})`);
      return { ...rows[0], creada: r.creadas > 0 };
    },
    onSuccess: (r) => {
      toast.success(r.creada ? `Variante ${r.codigo} creada` : `La variante ${r.codigo} ya existía — se usará`);
      onCreated(r.id);
    },
    onError: (e) => toast.error(getErrorMessage(e)),
  });

  return (
    <Modal title="Crear variante (color/talla)" onClose={onClose}>
      <div className="space-y-3">
        <Campo label="Modelo (producto padre) *">
          <select className={fieldCls} value={padreId} onChange={(e) => { setPadreId(e.target.value); setSeleccion({}); }}>
            <option value="">— Selecciona —</option>
            {padres.map((p) => <option key={p.id} value={p.id}>{p.codigo} · {p.nombre}</option>)}
          </select>
        </Campo>

        {padreId && atributos.length === 0 && (
          <p className="text-xs text-amber-600">La categoría de este modelo no tiene atributos configurados (Inventario → Catálogo → Atributos).</p>
        )}

        {atributos.map((a) => (
          <Campo key={a.id} label={`${a.nombre} *`}>
            <select className={fieldCls} value={seleccion[a.id] ?? ''} onChange={(e) => setSeleccion((s) => ({ ...s, [a.id]: e.target.value }))}>
              <option value="">— Selecciona —</option>
              {a.valores.map((v) => <option key={v.id} value={v.id}>{v.valor}</option>)}
            </select>
          </Campo>
        ))}

        <p className="text-xs text-muted-foreground">
          Se creará el SKU con su propio código y precio heredado del modelo; luego podrás ajustar precio/código de barras en Productos → Variantes.
        </p>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={crear.isPending} disabled={!completo} onClick={() => crear.mutate()}>Crear y usar</Button>
        </div>
      </div>
    </Modal>
  );
}

function CrearProductoModal({ onClose, onCreated }: { onClose: () => void; onCreated: (id: string) => void }) {
  const [f, setF] = useState({ codigo: '', nombre: '', unidad_medida: 'U', presentacion: '', fracciones: 1, iva_tarifa: 15, maneja_lote: false, maneja_caducidad: false });
  const set = (patch: Partial<typeof f>) => setF((s) => ({ ...s, ...patch }));
  const crear = useMutation({
    mutationFn: () => invRpc<{ id: string }>('inv_producto_crear_rapido', { payload: { ...f, iva_tarifa: Number(f.iva_tarifa), fracciones: Number(f.fracciones) || 1, presentacion: f.presentacion || null } }),
    onSuccess: (r) => { toast.success('Producto creado'); onCreated(r.id); },
    onError: (e) => toast.error(getErrorMessage(e)),
  });
  return (
    <Modal title="Crear producto" onClose={onClose}>
      <div className="space-y-3">
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Código (auto si vacío)"><input className={fieldCls} value={f.codigo} onChange={(e) => set({ codigo: e.target.value })} /></Campo>
          <Campo label="Unidad"><input className={fieldCls} value={f.unidad_medida} onChange={(e) => set({ unidad_medida: e.target.value })} /></Campo>
        </div>
        <Campo label="Nombre *"><input className={fieldCls} value={f.nombre} onChange={(e) => set({ nombre: e.target.value })} /></Campo>
        <div className="grid grid-cols-2 gap-3">
          <Campo label="Presentación"><input className={fieldCls} value={f.presentacion} placeholder="Ej: Caja x 14" onChange={(e) => set({ presentacion: e.target.value })} /></Campo>
          <Campo label="Fracciones x presentación"><input type="number" min="1" step="1" className={fieldCls} value={f.fracciones} onChange={(e) => set({ fracciones: Math.max(1, Math.trunc(toNum(e.target.value)) || 1) })} /></Campo>
        </div>
        <Campo label="IVA %"><input type="number" step="0.01" min="0" className={fieldCls} value={f.iva_tarifa} onChange={(e) => set({ iva_tarifa: toNum(e.target.value) })} /></Campo>
        <div className="grid grid-cols-2 gap-2">
          <Switch checked={f.maneja_lote} onChange={(v) => set({ maneja_lote: v })} label="Maneja lote" />
          <Switch checked={f.maneja_caducidad} onChange={(v) => set({ maneja_caducidad: v })} label="Maneja caducidad" />
        </div>
        <div className="flex justify-end gap-2 border-t pt-3">
          <Button variant="outline" onClick={onClose}>Cancelar</Button>
          <Button isLoading={crear.isPending} disabled={!f.nombre.trim()} onClick={() => crear.mutate()}>Crear y usar</Button>
        </div>
      </div>
    </Modal>
  );
}
